"use client";

import {useCallback} from "react";
import {useForm} from "react-hook-form";
import Container from "@/components/theme/container";
import {toast} from "sonner"
import * as z from "zod";
import {zodResolver} from "@hookform/resolvers/zod";
import {useGoogleReCaptcha} from "react-google-recaptcha-v3";
import {checkReCaptcha, sendContactUsEmail} from "@/app/_actions";
import {Form, FormControl, FormField, FormItem, FormMessage} from "@/components/ui/form";
import {Input} from "@/components/ui/input";
import {Textarea} from "@/components/ui/textarea";
import Link from "next/link";
import {SiteSettings} from "@/types/sanity";

export default function Contact({siteSettings, domain}: { siteSettings: SiteSettings, domain: string }) {

    const formSchema = z.object({
        userEmail: z.string().email(),
        name: z.string(),
        message: z.string()
    });


    const form = useForm<z.infer<typeof formSchema>>({
        resolver: zodResolver(formSchema),
        defaultValues: {
            userEmail: '',
            name: '',
            message: ''
        },
    })

    const {executeRecaptcha} = useGoogleReCaptcha();

    const handleReCaptchaVerify = useCallback(async () => {
        if (!executeRecaptcha) {
            return;
        }

        const token = await executeRecaptcha('contactUs');
        return await checkReCaptcha(token, domain);
    }, [executeRecaptcha, domain]);


    async function onSubmit(values: z.infer<typeof formSchema>) {

        const {message, userEmail, name} = values;
        try {
            if (await handleReCaptchaVerify()) {
                await sendContactUsEmail({
                    site: siteSettings.siteName,
                    message: message.trim(),
                    userEmail: userEmail.toLowerCase().trim(),
                    name
                })
                toast('Thank you for your submission!');
                form.reset();
            } else {
                toast(`Possible bot activity. Please try again or contact ${process.env.NEXT_PUBLIC_FEATURED_EMAIL} for support`);
            }


        } catch {
            toast(`An error occurred. Please try again or contact ${process.env.NEXT_PUBLIC_FEATURED_EMAIL} for support`);
        }
    }

    return (
        <Container>
            <h1 className="mt-2 mb-3 text-3xl font-semibold tracking-tight text-center lg:leading-snug text-brand-primary lg:text-4xl dark:text-white">
                Contact
            </h1>
            <div className="text-center">
                <p className="text-lg">We are a here to help.</p>
            </div>

            <div className="grid my-10 md:grid-cols-2">
                <div className="my-10">
                    <h2 className="text-2xl font-semibold dark:text-white">
                        Contact {siteSettings.siteName}
                    </h2>
                    <p className="max-w-sm mt-5">
                        Have something to say? We are here to help. Fill up the
                        form.
                    </p>
                </div>
                <div>
                    <Form {...form}>
                        <form onSubmit={form.handleSubmit(onSubmit)} className="my-10 flex flex-col">
                            <div className="mb-5">
                                <FormField
                                    control={form.control}
                                    name="name"
                                    render={({field}) => (
                                        <FormItem>
                                            <FormControl>
                                                <Input placeholder="Name" {...field}
                                                       exclusiveClassName={`w-full px-4 py-3 border-2 placeholder:text-gray-800 dark:text-white rounded-md outline-none dark:placeholder:text-gray-200 dark:bg-gray-900   focus:ring-4  ${
                                                           form.formState.errors.name
                                                               ? "border-red-600 focus:border-red-600 ring-red-100 dark:ring-0"
                                                               : "border-gray-300 focus:border-gray-600 ring-gray-100 dark:border-gray-600 dark:focus:border-white dark:ring-0"
                                                       }`}/>
                                            </FormControl>
                                            <FormMessage/>
                                        </FormItem>
                                    )}
                                />
                            </div>

                            <div className="mb-5">
                                <FormField
                                    control={form.control}
                                    name="userEmail"
                                    render={({field}) => (
                                        <FormItem>
                                            <FormControl>
                                                <Input placeholder="User Email" {...field}
                                                       exclusiveClassName={`w-full px-4 py-3 border-2 placeholder:text-gray-800 dark:text-white rounded-md outline-none dark:placeholder:text-gray-200 dark:bg-gray-900   focus:ring-4  ${
                                                           form.formState.errors.userEmail
                                                               ? "border-red-600 focus:border-red-600 ring-red-100 dark:ring-0"
                                                               : "border-gray-300 focus:border-gray-600 ring-gray-100 dark:border-gray-600 dark:focus:border-white dark:ring-0"
                                                       }`}/>
                                            </FormControl>
                                            <FormMessage/>
                                        </FormItem>
                                    )}
                                />
                            </div>

                            <div className="mb-3">
                                <FormField
                                    control={form.control}
                                    name="message"
                                    render={({field}) => (
                                        <FormItem>
                                            <FormControl>
                                                <Textarea placeholder={'Your Message'} {...field}
                                                          exclusiveClassName={`w-full px-4 py-3 border-2 placeholder:text-gray-800 dark:text-white dark:placeholder:text-gray-200 dark:bg-gray-900   rounded-md outline-none  h-36 focus:ring-4  ${
                                                              form.formState.errors.message
                                                                  ? "border-red-600 focus:border-red-600 ring-red-100 dark:ring-0"
                                                                  : "border-gray-300 focus:border-gray-600 ring-gray-100 dark:border-gray-600 dark:focus:border-white dark:ring-0"
                                                          }`}/>
                                            </FormControl>
                                            <FormMessage/>
                                        </FormItem>
                                    )}
                                />
                            </div>

                            <span
                                className={'text-sm leading-none py-2 mb-2'}>This site is protected by reCAPTCHA and the Google&nbsp;
                                <Link className={'text-primary'} target={'_blank'} rel={'noopener noreferrer'}
                                      href="https://policies.google.com/privacy"><strong>Privacy Policy</strong></Link>&nbsp;and&nbsp;
                                <Link
                                    className={'text-primary'}
                                    href="https://policies.google.com/terms" target={'_blank'}
                                    rel={'noopener noreferrer'}><strong>Terms of Service</strong></Link>&nbsp;apply.
                </span>


                            <button
                                type="submit"
                                className="w-full border py-4 font-semibold text-white transition-colors bg-gray-900 rounded-md hover:bg-gray-800 focus:outline-none focus:ring-offset-2 focus:ring focus:ring-gray-200 px-7 dark:bg-white dark:text-black ">
                                {form.formState.isSubmitting ? (
                                    <svg
                                        className="w-5 h-5 mx-auto text-white dark:text-black animate-spin"
                                        xmlns="http://www.w3.org/2000/svg"
                                        fill="none"
                                        viewBox="0 0 24 24">
                                        <circle
                                            className="opacity-25"
                                            cx="12"
                                            cy="12"
                                            r="10"
                                            stroke="currentColor"
                                            strokeWidth="4"></circle>
                                        <path
                                            className="opacity-75"
                                            fill="currentColor"
                                            d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                    </svg>
                                ) : (
                                    "Send Message"
                                )}
                            </button>

                        </form>
                    </Form>
                </div>
            </div>
        </Container>
    );
}
